/**
 * useNotificationSocket — Phase 18a.
 *
 * Connects to the notification-service WebSocket at
 * ``/api/v1/ws/notifications?token=…`` and surfaces incoming
 * notification payloads through an ``onNotification`` callback.
 * Reconnects with exponential backoff (1s, 2s, 4s, 8s, max 30s)
 * on unexpected drops.
 *
 * Returns a ``connected`` boolean for UI feedback (green dot on
 * the bell icon when real-time is live).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;

const buildWsUrl = (token) => {
  // Derive the ws base from the HTTP API base.
  const apiBase = process.env.REACT_APP_API_URL || '/api/v1';
  if (apiBase.startsWith('http')) {
    const url = new URL(apiBase);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${url.origin}/api/v1/ws/notifications?token=${encodeURIComponent(token)}`;
  }
  // Relative path — build from window.location.
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}${apiBase}/ws/notifications?token=${encodeURIComponent(token)}`;
};

const useNotificationSocket = ({ onNotification } = {}) => {
  const isAuthenticated = useSelector((s) => s.auth.isAuthenticated);
  const [connected, setConnected] = useState(false);

  const wsRef = useRef(null);
  const backoffRef = useRef(INITIAL_BACKOFF_MS);
  const reconnectTimer = useRef(null);
  // Store the latest callback in a ref so reconnects don't tear
  // down on every re-render.
  const onNotificationRef = useRef(onNotification);
  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  const cleanup = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    if (wsRef.current) {
      const ws = wsRef.current;
      wsRef.current = null;
      // Remove listeners before close so they don't fire during teardown.
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      // If the upgrade handshake is still mid-flight (CONNECTING state),
      // closing now triggers a "WebSocket is closed before the connection
      // is established" browser warning — common under React StrictMode
      // which mounts the effect twice in dev. Defer the close until the
      // socket actually opens, then close immediately.
      if (ws.readyState === WebSocket.CONNECTING) {
        ws.addEventListener('open', () => {
          try { ws.close(); } catch { /* ignore */ }
        }, { once: true });
      } else {
        try { ws.close(); } catch { /* ignore */ }
      }
    }
    setConnected(false);
  }, []);

  const connect = useCallback(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;

    const ws = new WebSocket(buildWsUrl(token));
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      backoffRef.current = INITIAL_BACKOFF_MS;
    };
    ws.onmessage = (event) => {
      let data;
      try { data = JSON.parse(event.data); } catch { return; }
      if (data.event === 'notification.created' && data.notification) {
        onNotificationRef.current?.(data.notification);
      }
      // Pings are dropped silently — the browser already keeps
      // the socket warm, we just don't need to act on them.
    };
    ws.onerror = () => {
      // onclose fires right after — let the close handler drive
      // the reconnect. This handler exists just to suppress the
      // default console noise.
    };
    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
      // Exponential backoff with jitter ±25% to spread reconnects.
      const jitter = 0.75 + Math.random() * 0.5;
      const delay = Math.min(
        MAX_BACKOFF_MS, backoffRef.current * jitter,
      );
      backoffRef.current = Math.min(MAX_BACKOFF_MS, backoffRef.current * 2);
      reconnectTimer.current = setTimeout(connect, delay);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      cleanup();
      return undefined;
    }
    connect();
    return cleanup;
  }, [isAuthenticated, connect, cleanup]);

  return { connected };
};

export default useNotificationSocket;
